<template>
  <ListComponent title="Users">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="px-0 pt-0 pb-2 d-flex align-center">
          <v-text-field
            v-model="filters.search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details="auto"
            clearable
            dense
            outlined
            @input="searchUsers"
            class="mr-2"
          ></v-text-field>

          <Columns :headers="headers" :gridColInstance="gridColumnApi" />
        </v-col>

        <v-col
          v-if="totalItems > 0"
          cols="8"
          class="px-0 pt-0 pb-2 d-flex justify-end"
        >
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>

        <v-col cols="12" class="pa-0">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 172px)"
            class="ag-theme-alpine"
            id="myGrid"
            :columnDefs="headers"
            :context="context"
            @grid-ready="onGridReady"
            @columnMoved="resizeGrid"
            @columnVisible="resizeGrid"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="allUserList"
          ></AgGridVue>
        </v-col>
      </v-row>

      <DetailsParent :resizable="true" v-model="detailsDrawer">
        <template #details>
          <UserDetails :userId="selectedUserId" />
        </template>
      </DetailsParent>
    </template>

    <template #actions>
      <v-btn
        v-if="permissions && permissions.add"
        color="primary"
        depressed
        @click="addUser"
      >
        <v-icon>mdi-plus</v-icon>
        <span>Add User</span>
      </v-btn>
    </template>
  </ListComponent>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import CustomerUserButton from "@/components/AgGridComponents/CustomerUserButton.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import UserDetails from "@/components/ModuleBased/details/UserDetails.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    Columns,
    AgGridVue,
    Pagination,
    CustomerUserButton,
    agColumnHeader: CustomHeader,
    DetailsParent,
    UserDetails,
  },
  data() {
    return {
      sorting: {},
      headers: [
        {
          headerName: "Username",
          field: "username",
          tooltipField: "username",
          sortable: true,
        },
        {
          headerName: "First Name",
          field: "first_name",
          tooltipField: "first_name",
          sortable: true,
        },
        {
          headerName: "Last Name",
          field: "last_name",
          tooltipField: "last_name",
          sortable: true,
        },
        { headerName: "Primary", field: "is_primary" },
        {
          headerName: "Role",
          field: "role_name",
          tooltipField: "role_name",
          sortable: true,
        },
        {
          headerName: "Designation",
          field: "designation",
          tooltipField: "designation",
          sortable: true,
        },
        {
          headerName: "Email",
          field: "email",
          tooltipField: "email",
          sortable: true,
        },
        {
          headerName: "Contact Number",
          field: "full_contact_number",
          tooltipField: "contact_number",
          sortable: true,
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "CustomerUserButton",
          pinned: "right",
          minWidth: 120,
        },
      ],
      allUserList: [],

      // pagination vars
      pageNo: 1,
      itemsPerPage: 20,
      totalItems: 0,

      // Filter vars
      filters: {},
      tab: null,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        width: 150,
        resizable: true,
        flex: 1,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
      permissions: null,
      selectedUserId: null,
      detailsDrawer: false,
    };
  },

  computed: {
    context() {
      return { parentComponent: this };
    },
    currentProfile() {
      let obj = JSON.parse(localStorage.getItem("userProfile"));
      if (!obj) {
        return null;
      } else {
        return obj;
      }
    },

    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },

  methods: {
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },
    resizeGrid() {
      if (this.gridOptions && "api" in this.gridOptions) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(20);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchUsers() {
      this.pageNo = 1;
      this.getUserList();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getUserList();
    },
    prevPage() {
      this.pageNo--;
      this.getUserList();
    },
    nextPage() {
      this.pageNo++;
      this.getUserList();
    },
    getUserList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        company: this.currentProfile.company,
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.user
        .getUserList(params)
        .then((res) => {
          this.allUserList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    addUser() {
      this.$router.push("/app/customer/user-management/add");
    },
    editUser(id) {
      this.$router.push(`/app/customer/user-management/edit/${id}`);
    },
    viewDetails(id) {
      this.selectedUserId = id;
      this.detailsDrawer = true;
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("user");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };
        this.getUserList();
      }
    }
  },
};
</script>
